import { RotateCcw } from 'lucide-react';
import { useEffect, useState } from 'react';

import { useToast } from '@/components/shared/Toast';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { mutationErrorMessage } from '@/features/common/apiError';
import {
  useAssignRoleMutation,
  useRefreshRoleMutation,
  useUnassignRoleMutation,
} from '@/features/roles/api/rolesApi';
import { RoleChecklist } from '@/features/roles/components/RoleChecklist';
import type { Role } from '@/features/roles/types';
import type { OrgMember } from '../types';

export interface AssignRolesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: string;
  member: OrgMember;
  roles: Role[];
  rolesLoading: boolean;
  /** Reports the member's applied role ids after every successful mutation. */
  onAppliedRoleIds: (userId: string, roleIds: string[]) => void;
}

/**
 * Assign roles dialog (design §10.2).
 *
 * Every toggle is applied **per row immediately** — ON → `POST /roles/:id/assign`,
 * OFF → `POST /roles/:id/unassign`, ↻ → `POST /roles/:id/refresh` — so there is no
 * batch to lose and Esc is always safe; “Done” simply closes. A row mid-flight
 * swaps its checkbox for a spinner, and a failure rolls the row back and toasts
 * (E1: never discard state). The rows come from the shared `RoleChecklist`, with
 * the renew button passed as the row's trailing control (§15.3).
 *
 * Backend reality: `/roles/:id/assign` writes a `roleAssignment` + mints the
 * ephemeral policies; it does **not** write back to `OrganizationMember.roleIds`
 * (the field the members table renders). The dialog therefore owns the applied set
 * and reports it upward, so the table agrees with what was just done instead of
 * showing a stale document field.
 */
export function AssignRolesDialog({
  open,
  onOpenChange,
  orgId,
  member,
  roles,
  rolesLoading,
  onAppliedRoleIds,
}: AssignRolesDialogProps) {
  const toast = useToast();
  const [applied, setApplied] = useState<string[]>(member.roleIds ?? []);
  /** Which row is mid-flight and doing what — drives the row's spinner. */
  const [pendingRoleId, setPendingRoleId] = useState<string | null>(null);
  const [assignRole] = useAssignRoleMutation();
  const [unassignRole] = useUnassignRoleMutation();
  const [refreshRole] = useRefreshRoleMutation();

  useEffect(() => {
    if (open) {
      setApplied(member.roleIds ?? []);
      setPendingRoleId(null);
    }
  }, [open, member.roleIds]);

  async function toggle(role: Role, checked: boolean) {
    const previous = applied;
    const next = checked ? [...previous, role._id] : previous.filter((value) => value !== role._id);
    setApplied(next);
    setPendingRoleId(role._id);

    try {
      const call = checked
        ? assignRole({ roleId: role._id, userId: member.userId, orgId })
        : unassignRole({ roleId: role._id, userId: member.userId, orgId });
      await call.unwrap();
      onAppliedRoleIds(member.userId, next);
    } catch (error) {
      setApplied(previous);
      toast.error(
        mutationErrorMessage(error, checked ? "Couldn't assign role." : "Couldn't unassign role."),
      );
    } finally {
      setPendingRoleId(null);
    }
  }

  async function renew(role: Role) {
    setPendingRoleId(role._id);
    try {
      await refreshRole({ roleId: role._id, userId: member.userId, orgId }).unwrap();
      toast.success('Role policies renewed.');
    } catch (error) {
      toast.error(mutationErrorMessage(error, "Couldn't renew role."));
    } finally {
      setPendingRoleId(null);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Assign roles</DialogTitle>
          <DialogDescription>Roles grant time-limited policies to this member.</DialogDescription>
        </DialogHeader>

        <RoleChecklist
          roles={roles}
          selectedRoleIds={applied}
          onToggle={(roleId, checked) => {
            const role = roles.find((candidate) => candidate._id === roleId);
            if (role) {
              void toggle(role, checked);
            }
          }}
          isLoading={rolesLoading}
          pendingRoleId={pendingRoleId}
          // Each row's checkbox is the server's own guard (`roles:assign` on
          // `role:${id}`): disabled with a reason once denied, silent while the
          // verdict is in flight (§14.5).
          gateRoleAssign
          renderTrailing={(role, denied) => (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0"
                  aria-label="Renew policies"
                  disabled={denied || pendingRoleId === role._id}
                  onClick={() => renew(role)}
                >
                  <RotateCcw className="h-4 w-4" aria-hidden="true" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Re-materialize this role's ephemeral policies</TooltipContent>
            </Tooltip>
          )}
        />

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
